import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Image from '../../../components/AppImage';

import { FileCode, HardDrive, Clock, Eye, GitBranch, Share2, Trash2 } from 'lucide-react';

const ProjectCard = ({ project, onDelete, onShare, onViewVersionHistory }) => {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);

  const handleViewFiles = () => {
    navigate('/file-explorer', { state: { projectId: project?.id } });
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = project?.zipUrl;
    link.download = `${project?.name}.zip`;
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const formatDuration = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    return `${minutes}m ${seconds % 60}s`;
  };

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden hover:shadow-lg transition-all duration-300 group">
      <div className="relative h-48 bg-muted overflow-hidden">
        <Image
          src={project?.thumbnail}
          alt={project?.thumbnailAlt || `Thumbnail for ${project?.name} project showing generated application interface`}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />

        {project?.isNew &&
        <span className="absolute top-3 right-3 px-2 py-1 bg-primary text-primary-foreground text-xs font-medium rounded-full">
            NEW
          </span>
        }
      </div>
      <div className="p-5">
        <h3 className="text-lg font-semibold text-foreground mb-2 line-clamp-1">
          {project?.name}
        </h3>
        <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
          {project?.prompt}
        </p>
        <div className="flex flex-wrap gap-2 mb-4">
          {project?.techStack?.slice(0, 3)?.map((tech, index) =>
          <span
              key={index}
              className="px-2 py-1 bg-secondary text-secondary-foreground text-xs rounded-md">

              {tech}
            </span>
          )}
          {project?.techStack?.length > 3 &&
          <span className="px-2 py-1 bg-secondary text-secondary-foreground text-xs rounded-md">
              +{project?.techStack?.length - 3}
            </span>
          }
        </div>
        <div className="flex items-center justify-between text-xs text-muted-foreground mb-4">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1">
              <FileCode className="h-3 w-3" />
              {project?.fileCount} files
            </span>
            <span className="flex items-center gap-1">
              <HardDrive className="h-3 w-3" />
              {project?.projectSize}
            </span>
          </div>
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {formatDate(project?.lastModified)}
          </span>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => navigate(`/file-explorer?project=${project?.id}`)}
            className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium flex items-center justify-center gap-2">

            <Eye className="h-4 w-4" />
            View
          </button>
          <button
            onClick={() => onViewVersionHistory?.(project)}
            className="px-4 py-2 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/80 transition-colors text-sm font-medium flex items-center justify-center gap-2"
            title="View Version History"
          >
            <GitBranch className="h-4 w-4" />
          </button>
          <button
            onClick={() => onShare?.(project)}
            className="px-4 py-2 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/80 transition-colors text-sm font-medium"
            title="Share Project">

            <Share2 className="h-4 w-4" />
          </button>
          <button
            onClick={() => onDelete?.(project?.id)}
            className="px-4 py-2 bg-destructive/10 text-destructive rounded-lg hover:bg-destructive/20 transition-colors text-sm font-medium"
            title="Delete Project">

            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProjectCard;